import { COUNTRIES } from '../data/countries';

export type PhoneInputValue = {
    countryCode: string;
    phone: string;
};

const DEFAULT_COUNTRY_CODE = 'EG';

export function emptyPhoneInput(): PhoneInputValue {
    return { countryCode: DEFAULT_COUNTRY_CODE, phone: '' };
}

export function normalizeE164Phone(value: string | null | undefined): string | null {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const normalized = raw.replace(/[\s()-]/g, '');
    return /^\+[1-9]\d{1,14}$/.test(normalized) ? normalized : null;
}

export function isValidE164Phone(value: string | null | undefined): boolean {
    return normalizeE164Phone(value) !== null;
}

/**
 * Minimum digits we expect after a country code for a value to plausibly be a
 * full international number. Egyptian mobiles are 10, UAE 9, India 10 — so a
 * remainder of 9+ means "the dial code is already there", while an Indian
 * national number like 9123456789 (dial 91 + only 8 left) is correctly kept as
 * a national number.
 */
const MIN_SUBSCRIBER_DIGITS = 9;

function isE164Digits(digits: string): boolean {
    return /^[1-9]\d{1,14}$/.test(digits);
}

/**
 * Splits an explicitly international value (`+20…`, `0020…`) into a country +
 * national part, matching the LONGEST dial code that still leaves digits behind.
 * Returns null when the value is not explicitly international or no dial code
 * matches — callers then treat it as a national number.
 */
export function splitInternationalPhone(value: string | null | undefined): PhoneInputValue | null {
    const compact = String(value || '').replace(/[\s()\-.]/g, '');
    if (!compact) return null;

    const isExplicit = compact.startsWith('+') || /^00\d/.test(compact);
    if (!isExplicit) return null;

    const digits = (compact.startsWith('+') ? compact.slice(1) : compact.slice(2)).replace(/[^\d]/g, '');
    if (!digits) return null;

    const match = COUNTRIES.reduce((best, entry) => {
        const dial = String(entry?.dial_code || '').replace(/[^\d]/g, '');
        if (!dial || dial.length >= digits.length || !digits.startsWith(dial)) return best;
        const bestDial = String(best?.dial_code || '').replace(/[^\d]/g, '');
        return !best || dial.length > bestDial.length ? entry : best;
    }, null as (typeof COUNTRIES)[number] | null);

    if (!match) return null;

    const dial = String(match.dial_code || '').replace(/[^\d]/g, '');
    return {
        countryCode: String(match.code || DEFAULT_COUNTRY_CODE),
        phone: digits.slice(dial.length).replace(/^0+/, '')
    };
}

export function phoneInputToE164(value: Partial<PhoneInputValue> | null | undefined): string | null {
    const raw = String(value?.phone || '').trim();
    if (!raw) return null;

    // "+20…" / "0020…" is an unambiguous international number — honour it as
    // typed instead of prefixing the selected country's dial code again.
    const international = splitInternationalPhone(raw);
    if (international?.phone) {
        return phoneInputToE164({ countryCode: international.countryCode, phone: international.phone });
    }

    const countryCode = String(value?.countryCode || '').trim().toUpperCase();
    const digits = raw.replace(/[^\d]/g, '').replace(/^0+/, '');
    if (!countryCode || !digits) return null;
    const country = COUNTRIES.find((entry) => entry.code === countryCode);
    const dialDigits = String(country?.dial_code || '').replace(/[^\d]/g, '');
    if (!dialDigits) return null;

    // The pasted value already carries this country's dial code (e.g. EG +20
    // selected and "201006541383" typed). Prefixing again produced numbers like
    // +20201006541383, which never match the existing user row and so created
    // duplicate leads/users.
    if (digits.startsWith(dialDigits) && digits.length >= dialDigits.length + MIN_SUBSCRIBER_DIGITS) {
        return isE164Digits(digits) ? `+${digits}` : null;
    }

    const candidate = `${dialDigits}${digits}`;
    return isE164Digits(candidate) ? `+${candidate}` : null;
}

export function e164ToPhoneInput(value: string | null | undefined): PhoneInputValue {
    const raw = String(value || '').trim();
    if (!raw || raw === '—' || raw === '-') return emptyPhoneInput();

    const compact = raw.replace(/[\s()\-.]/g, '');
    // Tolerate stored values that lost their leading "+" (e.g. "201006541383").
    const normalized = normalizeE164Phone(compact) || (/^\d{6,15}$/.test(compact) ? `+${compact}` : null);
    if (!normalized) return emptyPhoneInput();

    return (
        splitInternationalPhone(normalized) || {
            countryCode: DEFAULT_COUNTRY_CODE,
            phone: normalized.replace(/[^\d]/g, '')
        }
    );
}
